import type { indicator } from '../../../types/interfaces';
import {
  compareBpbCodesByOrder,
  GROUP_UNCATEGORIZED,
  normalizeBpbCategoryLabel,
} from './bpbIndicatorCategoryGroup';

export const DEFAULT_ATLANTIC_GROUP_ORDER = [
  'EVOLUCION_VENTAS',
  'RENTABILIDAD',
  'ENDEUDAMIENTO',
  'CAPITAL_TRABAJO',
  'FLUJO_FONDOS',
] as const;

export const DEFAULT_ATLANTIC_GROUP_TITLES: Record<string, string> = {
  EVOLUCION_VENTAS: 'Evolución Ventas',
  RENTABILIDAD: 'Rentabilidad',
  ENDEUDAMIENTO: 'Endeudamiento',
  CAPITAL_TRABAJO: 'Capital de Trabajo',
  FLUJO_FONDOS: 'Flujo de Fondos',
};

const ATLANTIC_KNOWN_KEYS = new Set<string>(DEFAULT_ATLANTIC_GROUP_ORDER);

/**
 * Atlantic-only label→key resolver. Does NOT reuse legacy Capital→SITUACION_FINANCIERA.
 * Membership SoT is DB `risk_indicator.category`; tenant_ui only orders/titles sections.
 */
export function resolveAtlanticGroupFromCategory(
  category: string | null | undefined
): string {
  if (category == null || String(category).trim() === '') {
    return GROUP_UNCATEGORIZED;
  }

  const trimmed = String(category).trim();
  const upperKey = trimmed.toUpperCase().replace(/\s+/g, '_');
  if (ATLANTIC_KNOWN_KEYS.has(upperKey)) {
    return upperKey;
  }

  const n = normalizeBpbCategoryLabel(trimmed);
  const nUnder = n.replace(/\s+/g, '_');

  if (
    nUnder === 'evolucion_ventas' ||
    nUnder === 'evolucionventas' ||
    /\bevolucion\s+ventas\b/.test(n)
  ) {
    return 'EVOLUCION_VENTAS';
  }
  if (nUnder === 'rentabilidad' || /\brentabilidad\b/.test(n)) {
    return 'RENTABILIDAD';
  }
  if (
    nUnder === 'endeudamiento' ||
    nUnder === 'apalancamiento' ||
    /\b(endeudamiento|apalancamiento)\b/.test(n)
  ) {
    return 'ENDEUDAMIENTO';
  }
  if (
    nUnder === 'capital_trabajo' ||
    nUnder === 'capitaldetrabajo' ||
    /\bcapital\s+de\s+trabajo\b/.test(n)
  ) {
    return 'CAPITAL_TRABAJO';
  }
  if (
    nUnder === 'flujo_fondos' ||
    nUnder === 'flujodefondos' ||
    /\bflujo\s+de\s+fondos\b/.test(n)
  ) {
    return 'FLUJO_FONDOS';
  }

  return GROUP_UNCATEGORIZED;
}

export type AtlanticGroupedIndicators = Map<
  string,
  { current: indicator[]; previous: indicator[] }
>;

/**
 * Groups by DB category via Atlantic resolver + groupOrder.
 * Empty accordion keys stay in the map (callers omit empty UI).
 * Null/unmapped → UNCATEGORIZED (KPI). Keys outside groupOrder are omitted.
 */
export function groupIndicatorsForAtlantic(
  current: indicator[],
  previous: indicator[],
  groupOrder: readonly string[] = DEFAULT_ATLANTIC_GROUP_ORDER,
  indicatorOrder?: Record<string, number>,
): AtlanticGroupedIndicators {
  const byGroup: AtlanticGroupedIndicators = new Map();
  for (const groupKey of groupOrder) {
    byGroup.set(groupKey, { current: [], previous: [] });
  }
  byGroup.set(GROUP_UNCATEGORIZED, { current: [], previous: [] });

  const place = (arr: indicator[], side: 'current' | 'previous') => {
    for (const ind of arr) {
      const group = resolveAtlanticGroupFromCategory(ind.category);
      const bucket = byGroup.get(group);
      if (!bucket) continue;
      bucket[side].push(ind);
    }
  };

  place(current, 'current');
  place(previous, 'previous');

  for (const g of byGroup.values()) {
    const currentByCode = new Map(g.current.map((i) => [i.code, i]));
    const previousByCode = new Map(g.previous.map((i) => [i.code, i]));
    const orderedCodes = [
      ...new Set([...currentByCode.keys(), ...previousByCode.keys()]),
    ].sort((codeA, codeB) => compareBpbCodesByOrder(codeA, codeB, indicatorOrder));
    g.current = orderedCodes
      .map((code) => currentByCode.get(code))
      .filter((i): i is indicator => i != null);
    g.previous = orderedCodes
      .map((code) => previousByCode.get(code))
      .filter((i): i is indicator => i != null);
  }

  return byGroup;
}
